import { outbox, type ReplayKeyField } from '@/lib/outbox';

/**
 * §28 / ADR-010 — the only place in the dashboard that writes.
 *
 * Nothing here calls `api.post` directly, and nothing outside this file should
 * either: every mutation is durably queued with a client-generated
 * `operationId` and only then flushed, so a dead spot in the back office costs
 * a spinner rather than a table's order. The four mobile apps hold the same
 * invariant, and it is the reason a lost write is a bug you can find — there is
 * exactly one funnel to look in.
 *
 * Replays are safe: the order and session services short-circuit on
 * `operationId` and are no-ops when the record is already in the target state
 * (P5-07), so a retry of a request that actually landed returns the original
 * row instead of creating a second one.
 *
 * Money never travels up: the client shows what it read, the server prices what
 * it charges (§38). No price, no total, no tax is ever in a body here.
 *
 * The one thing that must never appear in this file is a payment: anything
 * provider-bound has to fail loudly online rather than sit in a queue (P9-07).
 */

/**
 * Where each endpoint's replay key travels.
 *
 * Every route below declares `operationId` on its DTO — `OpenSessionDto`,
 * `CreateOrderDto` and the order action DTOs all do — so the key rides in the
 * body. A route that does **not** declare it needs `'header'` instead: the
 * API's `forbidNonWhitelisted` ValidationPipe answers 400 to a field a DTO has
 * not declared, and a 400 dead-letters the entry, so adding a mutation here
 * means checking its DTO rather than copying the line above it.
 */
const BODY: ReplayKeyField = 'operationId';

/** A catalog line as the dashboard composes it. Never carries a price (§38). */
export interface OrderLineInput {
  itemId: string;
  variantId?: string;
  quantity?: number;
  notes?: string;
  modifiers?: { modifierId: string; quantity?: number }[];
}

export interface CreateOrderInput {
  sessionId?: string;
  checkId?: string;
  branchId?: string;
  fulfillmentType?: 'DINE_IN' | 'TAKEAWAY';
  /** Who the bag is for. `CreateOrderDto` declares it; a dine-in order omits it. */
  customerName?: string;
  notes?: string;
  items: OrderLineInput[];
  /** §8.1 — confirm and fire in the same call; a DRAFT cannot reach the pass. */
  submit?: boolean;
}

/**
 * What the order routes answer with, narrowed to what a caller acts on.
 *
 * `checkId` is the field that matters to a till: it is null on a DRAFT and is
 * filled by `routeCharges` the moment the order is confirmed, so it is how the
 * register gets from "sent" to the bill it now has to settle without guessing.
 */
export interface PlacedOrder {
  id: string;
  number?: string;
  status?: string;
  checkId?: string | null;
}

export const mutations = {
  // ------------------------------------------------------------- sessions
  /**
   * §7 — open a table's session.
   *
   * `submit()` rather than `mutate()`: the caller needs the server-assigned id
   * to hang the order off, and `synced` is the only honest way to know whether
   * that id exists. Offline this still lands durably and answers
   * `synced: false` with no `data` — the screen must say so, not invent an id.
   */
  openSession: (input: { tableId?: string; guestCount?: number }) =>
    outbox.submit<{ id: string }>({
      method: 'post',
      url: '/sessions',
      body: input,
      replayField: BODY,
      label: 'session.open',
    }),

  // --------------------------------------------------------------- orders
  /**
   * §8 / ADR-003 — take an order against whatever context it belongs to: a
   * table session, a bill with no table at all, or a branch-level takeaway.
   *
   * `submit()`, deliberately, on both counts. It drains the queue **in order**,
   * so an order can never overtake the session-open still queued in front of
   * it — replaying the queue reproduces what the operator actually did, which
   * is the whole reason ordering is preserved. And it returns the server row,
   * so the caller can tell "sent to the kitchen" from "queued" instead of
   * claiming success for work that has not left the browser. `mutate()` is for
   * a till that must answer instantly while older work is stuck; a manager's
   * order screen would rather be correct than first.
   */
  createOrder: (input: CreateOrderInput) =>
    outbox.submit<PlacedOrder>({
      method: 'post',
      url: '/orders',
      body: input,
      replayField: BODY,
      label: 'order.create',
    }),

  /**
   * §8 — push more lines onto an order that has already been created.
   *
   * `submit()` for the same reason `createOrder` uses it, and here the reason is
   * sharper: these lines and the `sendOrder` that follows them are two steps of
   * one sequence, and a send that overtook a queued add would route an order the
   * kitchen cannot cook. `submit()` drains in order, so it physically cannot.
   * `AddOrderItemsDto` declares `operationId`, so the key rides in the body.
   */
  addOrderItems: (orderId: string, items: OrderLineInput[]) =>
    outbox.submit<PlacedOrder>({
      method: 'post',
      url: `/orders/${orderId}/items`,
      body: { items },
      replayField: BODY,
      label: 'order.add_items',
    }),

  /** §8.1 — fire an existing draft. A no-op server-side once it is already sent. */
  sendOrder: (orderId: string) =>
    outbox.submit<PlacedOrder>({
      method: 'post',
      url: `/orders/${orderId}/send`,
      body: {},
      replayField: BODY,
      label: 'order.send',
    }),

  // ---------------------------------------------------------- call-centre
  /**
   * ADR-0021 §2.1 — the call-centre desk on the same `OrdersService.create`
   * every other channel uses, through its own route so the agent's DTO can
   * carry a caller phone/address instead of a table. `submit: true` does
   * create-and-fire in one call server-side (unlike the till, which fires as a
   * second step), so this is the one write an order needs from this screen.
   *
   * `submit()`, not `mutate()`: a customer is on the line, but what they are
   * owed is a true answer, not a fast one, and `submit()` is the one that
   * drains the queue in order rather than racing ahead of older work — the
   * same trade `createOrder` above makes for the same reason. `CreateCallCenterOrderDto`
   * declares `operationId`, so the key rides in the body.
   */
  createCallCenterOrder: (input: CreateCallCenterOrderInput) =>
    outbox.submit<PlacedOrder>({
      method: 'post',
      url: '/call-center/orders',
      body: input,
      replayField: BODY,
      label: 'call_center.order_create',
    }),

  /**
   * ADR-0021 §2.9 — move a not-yet-fired order to a different branch.
   *
   * `mutate()`, not `submit()`: the agent asked a direct question ("can this
   * go to the other branch instead?") and needs the server's answer — menu
   * availability at the destination is re-checked live — not a queue position.
   * `TransferOrderBranchDto` declares no `operationId` field at all (the route
   * carries no `@OperationId()`), so a body key would 400 under
   * `forbidNonWhitelisted`; `replayField: 'none'` sends no replay key. That is
   * safe here specifically because the service's own transfer is a no-op when
   * `branchId` already matches — a duplicate delivery re-applies to the same
   * effect rather than transferring twice.
   */
  transferCallCenterOrder: (orderId: string, branchId: string) =>
    outbox.mutate<PlacedOrder>({
      method: 'post',
      url: `/call-center/orders/${orderId}/transfer`,
      body: { branchId },
      replayField: 'none',
      label: 'call_center.order_transfer_branch',
    }),
};

/**
 * `CreateCallCenterOrderDto`, mirrored. No price anywhere in it (§38) — every
 * line is priced from the catalog server-side, same as `CreateOrderInput`.
 */
export interface CallCenterOrderLineInput extends OrderLineInput {
  components?: { itemId: string; variantId?: string; quantity?: number; notes?: string }[];
}

export interface CreateCallCenterOrderInput {
  customerId?: string;
  customerPhone?: string;
  customerName?: string;
  branchId: string;
  fulfillmentType: 'TAKEAWAY' | 'DELIVERY';
  deliveryAddressId?: string;
  releaseStrategy?: 'IMMEDIATE' | 'SCHEDULED' | 'ON_CUSTOMER_REQUEST';
  scheduledFor?: string;
  tenderPreference?: 'CASH' | 'CARD' | 'WALLET' | 'BANK_TRANSFER' | 'VOUCHER' | 'LOYALTY_POINTS' | 'OTHER';
  notes?: string;
  items: CallCenterOrderLineInput[];
  submit?: boolean;
}

/**
 * Where a write that has not been acknowledged actually is.
 *
 * `Submitted.synced === false` covers three outcomes that mean three completely
 * different things to the person who pressed the button: it is on this device's
 * disk waiting for a connection, the server refused it on the merits and it is
 * in the dead letter, or a background flush delivered it between the press and
 * this question. "Saved, it will send" is a lie about the second one, and the
 * queue is the only thing that knows which happened — so it is asked, rather
 * than the screen assuming the kind one.
 */
export type WriteLocation<T> =
  /** Acknowledged. `data` is absent when a background flush delivered it. */
  | { settled: 'delivered'; data?: T }
  /** On this browser's disk, waiting for a connection. Never "lost". */
  | { settled: 'waiting' }
  /** Refused on the merits. Still in the dead letter unless the caller takes it. */
  | { settled: 'refused'; status: number | null; message: string; operationId: string };

/** Read the two stored lists. Sends nothing. */
export async function locateWrite<T>(operationId: string): Promise<WriteLocation<T>> {
  const refused = (await outbox.dead()).find((letter) => letter.entry.operationId === operationId);
  if (refused) {
    return {
      settled: 'refused',
      status: refused.status,
      message: refused.message,
      operationId,
    };
  }
  const waiting = (await outbox.all()).some((entry) => entry.operationId === operationId);
  return waiting ? { settled: 'waiting' } : { settled: 'delivered' };
}

/**
 * Drain the queue, then say where a held write ended up.
 *
 * This — not a second `submit()` — is what a retry of held work is. The entry is
 * already on disk under its own replay key: re-submitting it would put a second
 * copy of the same work in the queue, and re-submitting the same cart under a
 * *new* key would add the same lines twice.
 */
export async function resolveHeld<T>(operationId: string): Promise<WriteLocation<T>> {
  const flushed = await outbox.flush();
  const data = flushed.results[operationId];
  if (data !== undefined) return { settled: 'delivered', data: data as T };
  return locateWrite<T>(operationId);
}
